import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/site/Reveal";
import { useLang, lpath } from "@/lib/lang";

/* Lien interne ou mailto dans un texte : [[Libellé|/route]] ou [[contact@ostelo.fr|mailto:contact@ostelo.fr]] */
const Inline = ({ text, lang }) => {
  const parts = String(text).split(/\[\[(.+?)\|(.+?)\]\]/g);
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) out.push(parts[i]);
    const label = parts[i + 1];
    const to = parts[i + 2];
    if (label && to) {
      out.push(
        to.startsWith("mailto:") ? (
          <a key={i} href={to} className="font-semibold text-ostelo-blue underline-offset-2 hover:underline">
            {label}
          </a>
        ) : (
          <Link key={i} to={lpath(lang, to)} className="font-semibold text-ostelo-blue underline underline-offset-2 transition-colors hover:text-ostelo-orange">
            {label}
          </Link>
        )
      );
    }
  }
  return <>{out}</>;
};

const Para = ({ text, lang, className }) => (
  <p className={className}>
    {String(text).split("\n").map((line, i, arr) => (
      <span key={i}>
        <Inline text={line} lang={lang} />
        {i < arr.length - 1 ? <br /> : null}
      </span>
    ))}
  </p>
);

const CONTENT = {
  fr: {
    privacy: {
      intro: [
        "La présente politique de confidentialité a pour objectif d'informer les utilisateurs du site Ostelo sur la manière dont leurs données personnelles sont collectées, utilisées et protégées.",
        "Le site ostelo.fr et la solution Ostelo sont édités et exploités par BIVOAK, société par actions simplifiée (SAS) au capital social de 100 €, immatriculée au Registre du commerce et des sociétés de Manosque sous le numéro 104 638 374, dont le siège social est situé ZA Les Bastides Blanches, 12 rue du Dauphiné, 04220 Sainte-Tulle, France.",
        "BIVOAK agit en qualité de responsable du traitement des données personnelles collectées par l'intermédiaire du site.",
      ],
      sections: [
        {
          title: "1. Données personnelles collectées",
          blocks: [
            { p: "Dans le cadre de l'utilisation du site Ostelo, des données personnelles peuvent être collectées lorsque vous :" },
            { ul: ["remplissez un formulaire de contact ;", "demandez une démonstration de la solution Ostelo ;", "contactez notre équipe par e-mail ;", "échangez avec notre équipe dans le cadre d'une demande commerciale."] },
            { p: "Selon votre demande, les données collectées peuvent notamment comprendre :" },
            { ul: ["nom et prénom ;", "adresse e-mail professionnelle ;", "numéro de téléphone ;", "établissement ou entreprise ;", "fonction professionnelle ;", "contenu de votre demande ou de votre message."] },
            { p: "Certaines données techniques nécessaires au fonctionnement et à la sécurité du site peuvent également être traitées." },
          ],
        },
        {
          title: "2. Finalités des traitements",
          blocks: [
            { p: "Les données personnelles collectées peuvent être utilisées afin de :" },
            { ul: ["répondre aux demandes de contact ;", "traiter et organiser les demandes de démonstration ;", "fournir des informations concernant Ostelo et ses services ;", "assurer le suivi des échanges commerciaux ;", "gérer la relation avec nos clients et partenaires ;", "assurer le fonctionnement et la sécurité du site ;", "respecter les obligations légales et réglementaires applicables à BIVOAK."] },
            { p: "Les données ne sont pas utilisées à des fins incompatibles avec celles pour lesquelles elles ont été collectées." },
          ],
        },
        {
          title: "3. Bases légales",
          blocks: [
            { p: "Selon la nature du traitement, BIVOAK traite les données personnelles sur l'une des bases juridiques suivantes :" },
            { ul: ["le consentement, lorsqu'il est requis ;", "l'exécution de mesures précontractuelles, notamment lorsqu'une personne demande des informations ou une démonstration ;", "l'exécution d'un contrat, dans le cadre des relations avec nos clients et partenaires ;", "l'intérêt légitime de BIVOAK, notamment pour assurer le suivi des relations professionnelles et la sécurité de ses services ;", "le respect d'une obligation légale ou réglementaire."] },
          ],
        },
        {
          title: "4. Destinataires des données",
          blocks: [
            { p: "Les données personnelles collectées sont destinées aux personnes habilitées au sein de BIVOAK." },
            { p: "Elles peuvent également être traitées par des prestataires techniques intervenant pour le compte de BIVOAK lorsque cela est nécessaire au fonctionnement du site et des services associés, notamment dans les domaines de l'hébergement, de l'infrastructure, de la messagerie ou des services techniques." },
            { p: "Ces prestataires ne peuvent traiter les données que dans le cadre des prestations qui leur sont confiées." },
            { p: "BIVOAK ne vend ni ne loue les données personnelles de ses utilisateurs à des tiers." },
          ],
        },
        {
          title: "5. Durée de conservation",
          blocks: [
            { p: "Les données personnelles sont conservées uniquement pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées." },
            { p: "Les données relatives aux prospects peuvent notamment être conservées pendant une durée maximale de 3 ans à compter du dernier contact émanant du prospect, sous réserve des obligations légales applicables." },
            { p: "Les données relatives aux clients sont conservées pendant la durée de la relation contractuelle puis, lorsque cela est nécessaire, archivées pendant les durées imposées par les obligations légales, comptables et fiscales ou nécessaires à la constatation, l'exercice ou la défense de droits en justice." },
            { p: "À l'issue des durées applicables, les données sont supprimées ou anonymisées." },
          ],
        },
        {
          title: "6. Prestataires et transferts de données",
          blocks: [
            { p: "BIVOAK peut faire appel à différents prestataires techniques pour assurer le fonctionnement de ses services." },
            { p: "Lorsque ces prestataires traitent des données personnelles pour le compte de BIVOAK, ils interviennent en qualité de sous-traitants au sens du Règlement général sur la protection des données (RGPD)." },
            { p: "BIVOAK veille à recourir à des prestataires présentant des garanties appropriées en matière de protection et de sécurité des données." },
            { p: "Lorsque certaines données sont susceptibles d'être transférées en dehors de l'Espace économique européen, BIVOAK veille à ce que ces transferts reposent sur un mécanisme reconnu par la réglementation applicable et soient accompagnés, lorsque cela est nécessaire, de garanties appropriées." },
          ],
        },
        {
          title: "7. Sécurité",
          blocks: [
            { p: "BIVOAK met en œuvre des mesures techniques et organisationnelles adaptées afin de protéger les données personnelles contre notamment :" },
            { ul: ["l'accès non autorisé ;", "la divulgation ;", "la modification ;", "la perte ;", "la destruction accidentelle ou illicite."] },
            { p: "Ces mesures sont adaptées à la nature des données traitées et aux risques associés aux traitements." },
          ],
        },
        {
          title: "8. Vos droits",
          blocks: [
            { p: "Conformément au Règlement général sur la protection des données (RGPD) et à la législation française applicable, vous disposez, selon les conditions prévues par la réglementation, des droits suivants :" },
            { ul: ["droit d'accès ;", "droit de rectification ;", "droit à l'effacement ;", "droit à la limitation du traitement ;", "droit d'opposition ;", "droit à la portabilité lorsque celui-ci est applicable ;", "droit de retirer votre consentement à tout moment lorsqu'un traitement repose sur celui-ci."] },
            { p: "Vous pouvez également définir des directives relatives au sort de vos données personnelles après votre décès conformément à la législation française." },
          ],
        },
        {
          title: "9. Exercice de vos droits",
          blocks: [
            { p: "Pour exercer vos droits ou pour toute question relative au traitement de vos données personnelles :" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
            { p: "Afin de protéger vos données personnelles, un justificatif d'identité pourra être demandé lorsqu'il existe un doute raisonnable concernant l'identité de la personne effectuant la demande." },
            { p: "BIVOAK répond aux demandes dans les délais prévus par la réglementation applicable." },
          ],
        },
        {
          title: "10. Réclamation auprès de la CNIL",
          blocks: [
            { p: "Si vous estimez que le traitement de vos données personnelles ne respecte pas la réglementation applicable, vous disposez du droit d'introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés (CNIL)." },
          ],
        },
        {
          title: "11. Cookies et traceurs",
          blocks: [
            { p: "Les informations relatives aux cookies et autres traceurs utilisés sur le site sont présentées sur la page [[Gestion des cookies|/cookies]]." },
            { p: "Lorsqu'un cookie ou traceur nécessite le consentement préalable de l'utilisateur, celui-ci ne doit être déposé qu'après obtention de ce consentement conformément à la réglementation applicable." },
          ],
        },
        {
          title: "12. Modification de la politique",
          blocks: [
            { p: "BIVOAK se réserve la possibilité de modifier la présente politique de confidentialité afin notamment de tenir compte des évolutions du site, de ses services ou de la réglementation." },
            { p: "La date indiquée en haut de cette page correspond à la dernière mise à jour de la politique." },
          ],
        },
      ],
    },
    mentions: {
      intro: [],
      sections: [
        {
          title: "1. Éditeur du site",
          blocks: [
            { p: "Le site ostelo.fr est édité par :" },
            { p: "BIVOAK, société par actions simplifiée (SAS) au capital social de 100 €." },
            { p: "Siège social :\nZA Les Bastides Blanches\n12 rue du Dauphiné\n04220 Sainte-Tulle\nFrance" },
            { p: "SIREN : 104 638 374\nSIRET du siège : 104 638 374 00017\nRCS : Manosque – 104 638 374\nN° TVA intracommunautaire : FR48 104638374\nContact : [[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
            { p: "BIVOAK édite et exploite la solution Ostelo." },
          ],
        },
        {
          title: "2. Directeur de la publication",
          blocks: [
            { p: "Le directeur de la publication du site est :" },
            { p: "Gillian Benisti, en qualité de Président de BIVOAK." },
          ],
        },
        {
          title: "3. Hébergement",
          blocks: [
            { p: "Le site ostelo.fr est hébergé par :\nNetlify, Inc.\n44 Montgomery Street, Suite 300\nSan Francisco, California 94104\nÉtats-Unis" },
            { p: "Le nom de domaine ostelo.fr est enregistré auprès d'OVHcloud." },
            { p: "L'infrastructure technique peut également s'appuyer sur différents prestataires nécessaires à la gestion du DNS, de la messagerie et des services techniques associés." },
          ],
        },
        {
          title: "4. Propriété intellectuelle",
          blocks: [
            { p: "L'ensemble des éléments présents sur le site Ostelo, notamment sa structure, son identité visuelle, ses textes, graphismes, illustrations, logos, photographies, vidéos, interfaces, éléments logiciels et plus généralement son contenu, est protégé par les dispositions applicables en matière de propriété intellectuelle." },
            { p: "Sauf indication contraire, ces éléments sont la propriété de BIVOAK ou sont utilisés avec l'autorisation de leurs titulaires respectifs." },
            { p: "Toute reproduction, représentation, adaptation, modification, diffusion ou exploitation, totale ou partielle, du contenu du site sans autorisation préalable est interdite, sauf dans les cas expressément autorisés par la loi." },
            { p: "Les marques, noms commerciaux et logos de tiers éventuellement présents sur le site demeurent la propriété de leurs titulaires respectifs." },
          ],
        },
        {
          title: "5. Utilisation du site",
          blocks: [
            { p: "BIVOAK s'efforce de fournir sur le site Ostelo des informations aussi exactes et à jour que possible." },
            { p: "Toutefois, les informations présentes sur le site sont fournies à titre informatif et peuvent être modifiées à tout moment." },
            { p: "BIVOAK ne peut garantir l'absence totale d'erreurs, d'omissions ou d'indisponibilités temporaires du site." },
            { p: "BIVOAK se réserve le droit de modifier, suspendre ou interrompre tout ou partie du site lorsque cela est nécessaire, notamment pour des opérations de maintenance, de mise à jour ou pour toute autre raison technique." },
          ],
        },
        {
          title: "6. Responsabilité",
          blocks: [
            { p: "BIVOAK ne saurait être tenue responsable des dommages résultant d'une utilisation inappropriée du site, d'une incompatibilité technique avec l'équipement de l'utilisateur ou d'une interruption indépendante de sa volonté." },
            { p: "L'utilisateur est responsable de son équipement informatique, de sa connexion à Internet et des précautions nécessaires à la protection de ses équipements et données." },
          ],
        },
        {
          title: "7. Liens externes",
          blocks: [
            { p: "Le site peut contenir des liens permettant d'accéder à des sites ou services exploités par des tiers." },
            { p: "BIVOAK n'exerce aucun contrôle sur le contenu ou le fonctionnement de ces services externes et ne saurait être tenue responsable de leur contenu, de leur disponibilité ou de leurs pratiques." },
          ],
        },
        {
          title: "8. Données personnelles",
          blocks: [
            { p: "Les modalités de collecte, d'utilisation et de protection des données personnelles sont détaillées dans la page [[Politique de confidentialité|/politique-confidentialite]]." },
            { p: "Pour toute demande relative aux données personnelles :" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
          ],
        },
        {
          title: "9. Cookies",
          blocks: [
            { p: "Les informations concernant l'utilisation éventuelle de cookies et autres traceurs sont disponibles sur la page [[Gestion des cookies|/cookies]]." },
          ],
        },
        {
          title: "10. Droit applicable",
          blocks: [
            { p: "Le site et les présentes mentions légales sont soumis au droit français." },
            { p: "En cas de litige, les règles légales applicables en matière de compétence juridictionnelle s'appliquent." },
          ],
        },
      ],
    },
    cookies: {
      intro: [
        "La présente page informe les utilisateurs sur l'utilisation des cookies et autres traceurs susceptibles d'être utilisés lors de la navigation sur le site ostelo.fr, édité par BIVOAK.",
      ],
      sections: [
        {
          title: "1. Qu'est-ce qu'un cookie ?",
          blocks: [
            { p: "Un cookie est un petit fichier susceptible d'être enregistré sur l'appareil d'un utilisateur lors de la consultation d'un site internet." },
            { p: "Les cookies peuvent notamment permettre d'assurer le fonctionnement technique d'un site, de mémoriser certaines préférences ou, selon les services utilisés, de mesurer son audience." },
          ],
        },
        {
          title: "2. Cookies utilisés sur Ostelo",
          blocks: [
            { p: "À ce jour, ostelo.fr n'utilise pas de cookies publicitaires ni de traceurs destinés au ciblage publicitaire." },
            { p: "Le site peut uniquement utiliser des éléments de stockage ou cookies strictement nécessaires à son fonctionnement technique, à sa sécurité ou à la fourniture d'un service expressément demandé par l'utilisateur." },
            { p: "Conformément à la réglementation applicable, ces éléments strictement nécessaires ne requièrent pas le consentement préalable de l'utilisateur." },
          ],
        },
        {
          title: "3. Mesure d'audience",
          blocks: [
            { p: "À ce jour, Ostelo n'utilise pas de traceur de mesure d'audience nécessitant le consentement préalable de l'utilisateur." },
            { p: "Si BIVOAK décide ultérieurement de mettre en place un outil de mesure d'audience, son utilisation sera configurée conformément à la réglementation applicable." },
            { p: "Lorsque le consentement sera requis, le traceur concerné ne sera déposé qu'après obtention de ce consentement." },
            { p: "La présente page sera alors mise à jour." },
          ],
        },
        {
          title: "4. Cookies soumis au consentement",
          blocks: [
            { p: "Si des cookies ou traceurs non essentiels sont ajoutés ultérieurement au site, notamment à des fins de :" },
            { ul: ["mesure d'audience nécessitant un consentement ;", "personnalisation ;", "publicité ;", "intégration de services tiers déposant des traceurs ;"] },
            { p: "l'utilisateur sera informé de leur utilisation et pourra accepter ou refuser leur dépôt avant leur activation." },
            { p: "Le refus des cookies non essentiels n'empêchera pas l'accès aux fonctionnalités principales du site." },
          ],
        },
        {
          title: "5. Gestion des cookies depuis le navigateur",
          blocks: [
            { p: "L'utilisateur peut également configurer son navigateur afin de contrôler, bloquer ou supprimer les cookies enregistrés sur son appareil." },
            { p: "La désactivation de certains éléments strictement nécessaires peut toutefois affecter le fonctionnement de certaines fonctionnalités du site." },
          ],
        },
        {
          title: "6. Données personnelles",
          blocks: [
            { p: "Lorsque l'utilisation d'un cookie ou d'un traceur implique un traitement de données personnelles, celui-ci est réalisé conformément à notre [[Politique de confidentialité|/politique-confidentialite]] et à la réglementation applicable." },
          ],
        },
        {
          title: "7. Évolution de cette politique",
          blocks: [
            { p: "BIVOAK peut modifier la présente politique afin de tenir compte des évolutions techniques du site, de l'ajout de nouveaux services ou des évolutions réglementaires." },
            { p: "La date figurant en haut de cette page indique la dernière mise à jour." },
          ],
        },
        {
          title: "8. Contact",
          blocks: [
            { p: "Pour toute question concernant l'utilisation des cookies ou la protection des données personnelles :" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
          ],
        },
      ],
    },
  },
  en: {
    privacy: {
      intro: [
        "This privacy policy aims to inform users of the Ostelo website about how their personal data is collected, used and protected.",
        "The ostelo.fr website and the Ostelo solution are published and operated by BIVOAK, a simplified joint-stock company (SAS) with a share capital of €100, registered with the Manosque Trade and Companies Register under number 104 638 374, whose registered office is located at ZA Les Bastides Blanches, 12 rue du Dauphiné, 04220 Sainte-Tulle, France.",
        "BIVOAK acts as the data controller for personal data collected through the website.",
      ],
      sections: [
        {
          title: "1. Personal data collected",
          blocks: [
            { p: "When using the Ostelo website, personal data may be collected when you:" },
            { ul: ["fill in a contact form;", "request a demonstration of the Ostelo solution;", "contact our team by e-mail;", "communicate with our team as part of a sales enquiry."] },
            { p: "Depending on your request, the data collected may include:" },
            { ul: ["first and last name;", "professional e-mail address;", "phone number;", "establishment or company;", "professional role;", "the content of your request or message."] },
            { p: "Certain technical data necessary for the operation and security of the website may also be processed." },
          ],
        },
        {
          title: "2. Purposes of processing",
          blocks: [
            { p: "The personal data collected may be used to:" },
            { ul: ["respond to contact requests;", "process and organise demonstration requests;", "provide information about Ostelo and its services;", "follow up on commercial exchanges;", "manage relationships with our customers and partners;", "ensure the operation and security of the website;", "comply with the legal and regulatory obligations applicable to BIVOAK."] },
            { p: "Data is not used for purposes incompatible with those for which it was collected." },
          ],
        },
        {
          title: "3. Legal bases",
          blocks: [
            { p: "Depending on the nature of the processing, BIVOAK processes personal data on one of the following legal bases:" },
            { ul: ["consent, where required;", "the performance of pre-contractual measures, in particular when a person requests information or a demonstration;", "the performance of a contract, as part of relationships with our customers and partners;", "the legitimate interest of BIVOAK, in particular to follow up professional relationships and ensure the security of its services;", "compliance with a legal or regulatory obligation."] },
          ],
        },
        {
          title: "4. Data recipients",
          blocks: [
            { p: "The personal data collected is intended for authorised persons within BIVOAK." },
            { p: "It may also be processed by technical service providers acting on behalf of BIVOAK where necessary for the operation of the website and associated services, in particular in the areas of hosting, infrastructure, e-mail or technical services." },
            { p: "These providers may only process data within the scope of the services entrusted to them." },
            { p: "BIVOAK does not sell or rent its users' personal data to third parties." },
          ],
        },
        {
          title: "5. Retention period",
          blocks: [
            { p: "Personal data is kept only for the duration necessary for the purposes for which it was collected." },
            { p: "Data relating to prospects may be kept for a maximum of 3 years from the last contact initiated by the prospect, subject to applicable legal obligations." },
            { p: "Data relating to customers is kept for the duration of the contractual relationship and then, where necessary, archived for the periods imposed by legal, accounting and tax obligations or necessary for the establishment, exercise or defence of legal claims." },
            { p: "At the end of the applicable periods, data is deleted or anonymised." },
          ],
        },
        {
          title: "6. Service providers and data transfers",
          blocks: [
            { p: "BIVOAK may use various technical service providers to operate its services." },
            { p: "Where these providers process personal data on behalf of BIVOAK, they act as processors within the meaning of the General Data Protection Regulation (GDPR)." },
            { p: "BIVOAK ensures that it uses providers offering appropriate guarantees regarding data protection and security." },
            { p: "Where certain data may be transferred outside the European Economic Area, BIVOAK ensures that such transfers rely on a mechanism recognised by applicable regulations and are accompanied, where necessary, by appropriate safeguards." },
          ],
        },
        {
          title: "7. Security",
          blocks: [
            { p: "BIVOAK implements appropriate technical and organisational measures to protect personal data against, in particular:" },
            { ul: ["unauthorised access;", "disclosure;", "modification;", "loss;", "accidental or unlawful destruction."] },
            { p: "These measures are adapted to the nature of the data processed and the risks associated with the processing." },
          ],
        },
        {
          title: "8. Your rights",
          blocks: [
            { p: "In accordance with the General Data Protection Regulation (GDPR) and applicable French legislation, you have, subject to the conditions provided by the regulations, the following rights:" },
            { ul: ["right of access;", "right to rectification;", "right to erasure;", "right to restriction of processing;", "right to object;", "right to data portability where applicable;", "right to withdraw your consent at any time where processing is based on it."] },
            { p: "You may also define directives regarding the fate of your personal data after your death, in accordance with French legislation." },
          ],
        },
        {
          title: "9. Exercising your rights",
          blocks: [
            { p: "To exercise your rights or for any question relating to the processing of your personal data:" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
            { p: "In order to protect your personal data, proof of identity may be requested where there is reasonable doubt about the identity of the person making the request." },
            { p: "BIVOAK responds to requests within the timeframes provided by applicable regulations." },
          ],
        },
        {
          title: "10. Complaint to the CNIL",
          blocks: [
            { p: "If you believe that the processing of your personal data does not comply with applicable regulations, you have the right to lodge a complaint with the Commission nationale de l'informatique et des libertés (CNIL)." },
          ],
        },
        {
          title: "11. Cookies and trackers",
          blocks: [
            { p: "Information about cookies and other trackers used on the website is presented on the [[Cookie management|/cookies]] page." },
            { p: "Where a cookie or tracker requires the user's prior consent, it may only be placed after such consent has been obtained, in accordance with applicable regulations." },
          ],
        },
        {
          title: "12. Changes to this policy",
          blocks: [
            { p: "BIVOAK reserves the right to modify this privacy policy, in particular to take into account changes to the website, its services or regulations." },
            { p: "The date shown at the top of this page corresponds to the latest update of the policy." },
          ],
        },
      ],
    },
    mentions: {
      intro: [],
      sections: [
        {
          title: "1. Site publisher",
          blocks: [
            { p: "The ostelo.fr website is published by:" },
            { p: "BIVOAK, a simplified joint-stock company (SAS) with a share capital of €100." },
            { p: "Registered office:\nZA Les Bastides Blanches\n12 rue du Dauphiné\n04220 Sainte-Tulle\nFrance" },
            { p: "SIREN: 104 638 374\nSIRET (registered office): 104 638 374 00017\nRCS: Manosque – 104 638 374\nIntra-community VAT no.: FR48 104638374\nContact: [[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
            { p: "BIVOAK publishes and operates the Ostelo solution." },
          ],
        },
        {
          title: "2. Publication director",
          blocks: [
            { p: "The publication director of the website is:" },
            { p: "Gillian Benisti, in his capacity as President of BIVOAK." },
          ],
        },
        {
          title: "3. Hosting",
          blocks: [
            { p: "The ostelo.fr website is hosted by:\nNetlify, Inc.\n44 Montgomery Street, Suite 300\nSan Francisco, California 94104\nUnited States" },
            { p: "The ostelo.fr domain name is registered with OVHcloud." },
            { p: "The technical infrastructure may also rely on various providers necessary for DNS management, e-mail and associated technical services." },
          ],
        },
        {
          title: "4. Intellectual property",
          blocks: [
            { p: "All elements present on the Ostelo website, including its structure, visual identity, texts, graphics, illustrations, logos, photographs, videos, interfaces, software elements and more generally its content, are protected by applicable intellectual property provisions." },
            { p: "Unless otherwise stated, these elements are the property of BIVOAK or are used with the authorisation of their respective owners." },
            { p: "Any reproduction, representation, adaptation, modification, distribution or exploitation, in whole or in part, of the website content without prior authorisation is prohibited, except in cases expressly authorised by law." },
            { p: "Third-party trademarks, trade names and logos that may appear on the website remain the property of their respective owners." },
          ],
        },
        {
          title: "5. Use of the website",
          blocks: [
            { p: "BIVOAK strives to provide information on the Ostelo website that is as accurate and up to date as possible." },
            { p: "However, the information on the website is provided for information purposes only and may be modified at any time." },
            { p: "BIVOAK cannot guarantee the total absence of errors, omissions or temporary unavailability of the website." },
            { p: "BIVOAK reserves the right to modify, suspend or interrupt all or part of the website where necessary, in particular for maintenance, updates or any other technical reason." },
          ],
        },
        {
          title: "6. Liability",
          blocks: [
            { p: "BIVOAK cannot be held liable for damage resulting from inappropriate use of the website, technical incompatibility with the user's equipment or an interruption beyond its control." },
            { p: "The user is responsible for their computer equipment, their Internet connection and the precautions necessary to protect their equipment and data." },
          ],
        },
        {
          title: "7. External links",
          blocks: [
            { p: "The website may contain links providing access to websites or services operated by third parties." },
            { p: "BIVOAK exercises no control over the content or operation of these external services and cannot be held responsible for their content, availability or practices." },
          ],
        },
        {
          title: "8. Personal data",
          blocks: [
            { p: "The terms of collection, use and protection of personal data are detailed on the [[Privacy Policy|/politique-confidentialite]] page." },
            { p: "For any request relating to personal data:" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
          ],
        },
        {
          title: "9. Cookies",
          blocks: [
            { p: "Information concerning the possible use of cookies and other trackers is available on the [[Cookie management|/cookies]] page." },
          ],
        },
        {
          title: "10. Applicable law",
          blocks: [
            { p: "The website and these legal notices are governed by French law." },
            { p: "In the event of a dispute, the applicable legal rules on jurisdictional competence apply." },
          ],
        },
      ],
    },
    cookies: {
      intro: [
        "This page informs users about the use of cookies and other trackers that may be used when browsing the ostelo.fr website, published by BIVOAK.",
      ],
      sections: [
        {
          title: "1. What is a cookie?",
          blocks: [
            { p: "A cookie is a small file that may be stored on a user's device when visiting a website." },
            { p: "Cookies may in particular be used to ensure the technical operation of a site, remember certain preferences or, depending on the services used, measure its audience." },
          ],
        },
        {
          title: "2. Cookies used on Ostelo",
          blocks: [
            { p: "To date, ostelo.fr does not use advertising cookies or trackers intended for advertising targeting." },
            { p: "The website may only use storage elements or cookies strictly necessary for its technical operation, its security or the provision of a service expressly requested by the user." },
            { p: "In accordance with applicable regulations, these strictly necessary elements do not require the user's prior consent." },
          ],
        },
        {
          title: "3. Audience measurement",
          blocks: [
            { p: "To date, Ostelo does not use any audience measurement tracker requiring the user's prior consent." },
            { p: "If BIVOAK later decides to implement an audience measurement tool, its use will be configured in accordance with applicable regulations." },
            { p: "Where consent is required, the tracker concerned will only be placed after such consent has been obtained." },
            { p: "This page will then be updated." },
          ],
        },
        {
          title: "4. Cookies subject to consent",
          blocks: [
            { p: "If non-essential cookies or trackers are added to the website in the future, in particular for the purposes of:" },
            { ul: ["audience measurement requiring consent;", "personalisation;", "advertising;", "integration of third-party services placing trackers;"] },
            { p: "the user will be informed of their use and will be able to accept or refuse them before they are activated." },
            { p: "Refusing non-essential cookies will not prevent access to the main features of the website." },
          ],
        },
        {
          title: "5. Managing cookies from your browser",
          blocks: [
            { p: "Users can also configure their browser to control, block or delete cookies stored on their device." },
            { p: "However, disabling certain strictly necessary elements may affect the operation of certain features of the website." },
          ],
        },
        {
          title: "6. Personal data",
          blocks: [
            { p: "Where the use of a cookie or tracker involves the processing of personal data, this is carried out in accordance with our [[Privacy Policy|/politique-confidentialite]] and applicable regulations." },
          ],
        },
        {
          title: "7. Changes to this policy",
          blocks: [
            { p: "BIVOAK may modify this policy to take into account technical changes to the website, the addition of new services or regulatory developments." },
            { p: "The date shown at the top of this page indicates the latest update." },
          ],
        },
        {
          title: "8. Contact",
          blocks: [
            { p: "For any question concerning the use of cookies or the protection of personal data:" },
            { p: "[[contact@ostelo.fr|mailto:contact@ostelo.fr]]" },
          ],
        },
      ],
    },
  },
};

export default function Legal({ kind }) {
  const { t } = useTranslation();
  const lang = useLang();
  const title = t(`legal.${kind}_title`);
  const content = CONTENT[lang]?.[kind] || CONTENT.fr[kind];

  return (
    <>
      <Seo title={`${title} — Ostelo`} description={title} lang={lang} />
      <section className="px-6 pb-20 pt-[60px] lg:px-10 lg:pb-28 lg:pt-[83px]" data-testid={`legal-${kind}`}>
        <Reveal className="mx-auto max-w-3xl">
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-ostelo-deep sm:text-5xl">{title}</h1>
          <p className="mt-4 text-xs uppercase tracking-[0.15em] text-ostelo-ink/40">{t("legal.updated")}</p>
          <div className="mt-10 space-y-5">
            {content.intro.map((p, i) => (
              <Para key={i} text={p} lang={lang} className="text-sm leading-relaxed text-ostelo-ink/70" />
            ))}
          </div>
          <div className="mt-12 space-y-10">
            {content.sections.map((s) => (
              <section key={s.title}>
                <h2 className="font-display text-lg font-bold tracking-tight text-ostelo-deep sm:text-xl">{s.title}</h2>
                <div className="mt-4 space-y-4">
                  {s.blocks.map((b, i) =>
                    b.ul ? (
                      <ul key={i} className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-ostelo-ink/70">
                        {b.ul.map((item, j) => (
                          <li key={j}>
                            <Inline text={item} lang={lang} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Para key={i} text={b.p} lang={lang} className="text-sm leading-relaxed text-ostelo-ink/70" />
                    )
                  )}
                </div>
              </section>
            ))}
          </div>
        </Reveal>
      </section>
    </>
  );
}
